import type { Opt } from '@jet/environment/types/optional';
import { URL } from '@jet/environment/util/urls';
import type { Localization } from '../../dependencies/localization';
import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies';
import {
    ARTWORK_META_TAG_DEFAULTS,
    SHARING_ARTWORK_FALLBACK,
} from './constants';
import type { SeoData } from './types';

/**
 * General utility to access the base page title.  Used as a fallback
 * value by library detail routes that do not need to generate
 * SEO data but still need to provide a unique page title
 * without having to get the entire base SEO object back.
 *
 * @method getBasePageTitle
 * @param objectGraph
 * @returns {String}
 */
export function getBasePageTitle(
    localization: Localization,
    siteName: string,
): string {
    return localization.string(
        'ASE.Web.MusicClassicalClientJS.Meta.Common.PageTitle',
        { siteName },
    );
}

/**
 * @method getBaseSeoData Generates object with default SEO metadata values
 * @param {string} url canonical url, usually from page object being updated
 * @param {ObjectGraph} objectGraph
 * @param {object} [customAttributes] override base seo data values
 * @returns {SeoData}
 */
export function getBaseSeoData(
    url: string,
    objectGraph: ObjectGraph,
    customAttributes: object = {},
): SeoData {
    const localization: Localization = inject(
        ObjectGraphType.localization,
        objectGraph,
    );
    const siteName = localization.string('ASE.Web.MusicClassicalClientJS.Name');
    const title = getBasePageTitle(localization, siteName);
    const description = localization.string(
        'ASE.Web.MusicClassicalClientJS.Meta.Home.Description',
    );

    return {
        url: removeLanguageQueryParam(url),
        noIndex: false,
        noFollow: false,
        keywords: undefined,
        siteName: title,
        pageTitle: title,
        description,
        // Artwork properties
        fileType: ARTWORK_META_TAG_DEFAULTS.fileType,
        artworkUrl: SHARING_ARTWORK_FALLBACK.url,
        crop: ARTWORK_META_TAG_DEFAULTS.crop,
        height: SHARING_ARTWORK_FALLBACK.height,
        width: SHARING_ARTWORK_FALLBACK.width,
        // TODO: rdar://97913309 (JMOTW: Artwork: Quality Param regex injects quality placeholder when no hardcoded quality param exists)
        //       can default quality value once this radar is resolved
        // quality: ARTWORK_META_TAG_DEFAULTS.quality,
        // Apple-specific properties
        appleStoreId: '1598433714',
        appleStoreName: siteName,
        appleContentId: undefined,
        appleTitle: title,
        appleDescription: description,
        // OpenGraph properties
        ogType: undefined,
        socialTitle: title,
        socialDescription: description,
        // Twitter properties
        twitterSite: '@AppleClassical',
        twitterCardType: 'summary_large_image',
        twitterWidth: ARTWORK_META_TAG_DEFAULTS.width,
        twitterHeight: ARTWORK_META_TAG_DEFAULTS.height,
        twitterCropCode: ARTWORK_META_TAG_DEFAULTS.crop,
        // Schema.org properties
        showOrgData: false, // Classical at this time will not provide org data
        // Custom overrides
        ...customAttributes,
    };
}

function removeLanguageQueryParam(urlString: Opt<string>): Opt<string> {
    if (urlString) {
        const url = new URL(urlString);
        if (url?.query) {
            delete url.query.l;
        }
        return url.toString();
    }
    return undefined;
}
